import { Settings, Shield, Terminal, Wrench } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useState, useEffect } from "react";

export default function MaintenancePage() {
  const { t } = useI18n();
  const [dots, setDots] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((d) => (d.length >= 3 ? "" : d + "."));
    }, 600);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-maintenance-gradient flex items-center justify-center p-4 relative overflow-hidden" style={{ touchAction: 'none' }}>

      {/* Ambient glow orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/3 right-1/4 w-80 h-80 bg-orange-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-amber-500/3 rounded-full blur-3xl pointer-events-none" />

      {/* Scanline overlay */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden opacity-[0.03]">
        <div className="w-full h-32 bg-gradient-to-b from-transparent via-amber-400 to-transparent" style={{ animation: 'scanline 8s linear infinite' }} />
      </div>

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: 'linear-gradient(rgba(245,158,11,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.3) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      {/* Floating decorative gears */}
      <div className="absolute top-16 right-20 opacity-[0.04] pointer-events-none hidden md:block">
        <Settings className="w-24 h-24 text-amber-400" style={{ animation: 'gearSpin 25s linear infinite' }} />
      </div>
      <div className="absolute bottom-20 left-16 opacity-[0.04] pointer-events-none hidden md:block">
        <Settings className="w-16 h-16 text-amber-400" style={{ animation: 'gearSpinReverse 20s linear infinite' }} />
      </div>
      <div className="absolute top-1/3 right-10 opacity-[0.03] pointer-events-none hidden lg:block">
        <Settings className="w-10 h-10 text-amber-400" style={{ animation: 'gearSpin 15s linear infinite' }} />
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-md" style={{ animation: 'floatSlow 6s ease-in-out infinite' }}>

        {/* Icon cluster */}
        <div className="flex items-center justify-center mb-8">
          <div className="relative">
            {/* Outer ring pulse */}
            <div className="absolute inset-0 w-24 h-24 rounded-2xl bg-amber-500/10 animate-ping" style={{ animationDuration: '3s' }} />

            {/* Icon container */}
            <div className="relative w-24 h-24 rounded-2xl bg-gradient-to-br from-amber-500/20 via-orange-500/15 to-red-500/10 flex items-center justify-center border border-amber-500/20 backdrop-blur-sm shadow-2xl shadow-amber-500/10">
              <Settings className="w-11 h-11 text-amber-400" style={{ animation: 'gearSpin 4s linear infinite' }} />
            </div>

            {/* Status dot */}
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full border-2 border-zinc-950 flex items-center justify-center">
              <Wrench className="w-2.5 h-2.5 text-zinc-950" />
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-mono font-bold text-white tracking-tight mb-3" data-testid="maintenance-title">
            {t.maintenanceTitle}
          </h1>
          <p className="text-zinc-400 font-mono text-sm leading-relaxed max-w-sm mx-auto">
            {t.maintenanceDesc}
          </p>
        </div>

        {/* Card */}
        <div className="bg-zinc-900/50 backdrop-blur-xl border border-zinc-800/80 rounded-2xl p-6 shadow-2xl shadow-black/40 mb-6">

          {/* Progress indicator */}
          <div className="mb-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-mono text-amber-400/80 uppercase tracking-widest">Maintenance en cours</span>
              <span className="text-[10px] font-mono text-zinc-500">{dots.padEnd(3, '\u00A0')}</span>
            </div>
            <div className="w-full h-1 bg-zinc-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-500 via-orange-400 to-amber-500 rounded-full" style={{ animation: 'progressPulse 3s ease-in-out infinite' }} />
            </div>
          </div>

          {/* Status items */}
          <div className="space-y-3">
            <div className="flex items-center gap-3 text-xs font-mono">
              <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                <Shield className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <div>
                <p className="text-zinc-300">Systèmes sécurisés</p>
                <p className="text-zinc-600 text-[10px]">Vos données sont protégées</p>
              </div>
              <div className="ml-auto w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            </div>

            <div className="flex items-center gap-3 text-xs font-mono">
              <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                <Wrench className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <div>
                <p className="text-zinc-300">Mise à jour en cours</p>
                <p className="text-zinc-600 text-[10px]">Améliorations et correctifs</p>
              </div>
              <div className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
            </div>

            <div className="flex items-center gap-3 text-xs font-mono">
              <div className="w-7 h-7 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
                <Terminal className="w-3.5 h-3.5 text-amber-400" />
              </div>
              <div>
                <p className="text-zinc-300">Retour imminent</p>
                <p className="text-zinc-600 text-[10px]">{t.maintenanceBack}</p>
              </div>
              <div className="ml-auto w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" style={{ animationDelay: '0.5s' }} />
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
          <div className="w-1.5 h-1.5 rounded-full bg-amber-500/40" />
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent" />
        </div>

        {/* Footer branding */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-600/20 border border-cyan-500/30 flex items-center justify-center overflow-hidden">
              <img src="/icons/icon-96.png" alt="" className="w-4 h-4 object-contain" onError={(e) => { e.target.style.display = "none"; }} />
            </div>
            <p className="text-xs font-mono font-semibold">
              <span className="text-zinc-600">&lt;</span>
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">ShadowMail</span>
              <span className="text-zinc-600"> /&gt;</span>
            </p>
          </div>
          <p className="text-zinc-600 text-[10px] font-mono">
            Anonyme &middot; Chiffré &middot; Sécurisé
          </p>
        </div>
      </div>
    </div>
  );
}
